import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetLoggedInUser, useUserTrialDetails } from '@revelio/auth';

import { UnsavedChangesDialog } from '../../shared/components/unsaved-changes-dialog';
import { useToast } from '../../shared/hooks/useToast';
import {
  type ActivityFilterFlags,
  useActivityFilterFlags,
} from '../../shared/unleash-flags/use-activity-filter-flags';
import { useUnleashFlags } from '../../shared/unleash-flags/use-unleash-flag';
import { useUnleashReady } from '../../shared/unleash-flags/use-unleash-ready';
import { usePageManager } from '../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { useReportBuilderConfig } from '../reports/hooks/useReportBuilderConfig';
import { AssistantEngine, getCurrentRoute } from './assistant-engine-core';
import { postGlobalAssistantWarmUp } from './global-ai-assistant-api';
import { useGlobalAiCommandStore } from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import { requestGlobalAssistantWarmUpOnce } from './global-ai-assistant-warm-up';
import { useGlobalAiConversationStateStore } from './global-ai-conversation-state-store';
import { warmHeadlessDigestPath } from './headless-digest/headless-digest-service';
import {
  useGlobalAssistantTaxonomy,
  useGlobalAssistantTaxonomyReady,
} from './use-global-ai-assistant-taxonomy';
import { useGlobalAiTurnContinuation } from './use-global-ai-turn-continuation';
import { useGlobalAiTurnRunner } from './use-global-ai-turn-runner';

/**
 * React SHELL for the global AI assistant's headless conversation engine
 * (renders only the in-app unsaved-changes dialog the redirect guard opens).
 *
 * The orchestration itself lives in `AssistantEngine`
 * (assistant-engine-core.ts) — turn running/submission, the tool-loop runner's
 * dep set, navigation + route-filter application, offer/report launch, the
 * channel drains. This component owns only what genuinely needs React: reading
 * the hooks the engine depends on and pushing them into the instance
 * (`updateDeps`, in the render body — H1), the warm-up idle effect, the two
 * channel-drain effects' scheduling, the cross-navigation continuation hook,
 * and mount/unmount (publish the instance in the store, dispose it on
 * teardown).
 *
 * The chat skins (floating drawer, assistant homepage) are pure renderers over
 * the assistant context plus this engine's store: the host mounts ONE engine
 * instance whenever any surface is active or a conversation exists, so an
 * in-flight turn keeps streaming while the user moves between the homepage and
 * the drawer.
 */
export function GlobalAiAssistantEngine() {
  const {
    turnAbortRef,
    streamAbortRef,
    hasRequestedWarmUp,
    markWarmUpRequested,
    lastUserPrompt,
    setLastUserPrompt,
    setGenerationStatus,
    generationStatus,
    appendTranscript,
    updateReasoningTrace,
    updateAssistantMessageText,
    clearTranscript,
    sessionEntityScope,
    customDashAssistContextRef,
    conversationSummary,
    conversationTrackingIdRef,
  } = useGlobalAIAssistant();

  // Per-entity taxonomy resolver — SAVED PREFS ONLY, single source of truth.
  //
  // Request taxonomy must equal what the destination page's pref-sync will
  // apply (the scoped stored preference): that invariant is what keeps
  // text = artifact link = landed page consistent. The bridge-published
  // `activeTaxonomyByEntity` used to override prefs here, which made the
  // request taxonomy depend on WHICH page happened to be mounted when the
  // user asked — same account, same question, different answers. Known
  // trade-off (product decision): session-only unsaved overrides from the
  // Preferences modal are no longer reflected in assistant requests.
  //
  // The bridge PUBLISH itself stays: `waitForTaxonomyChange` (the
  // adaptive-company refinement pass in the core's
  // `resolveThenEmitRouteFilters`) still consumes the store's
  // `activeTaxonomyByEntity` to detect post-apply adaptive re-resolution.
  // TODO(backend-adaptive): delete the publish path with that refinement
  // block once the backend accepts "adaptive".
  const taxonomyByEntity = useGlobalAssistantTaxonomy();
  // Passed to the engine core as a plain dep (refreshed every render): its
  // taxonomy-hold poll re-reads it per iteration, so it needs no mirror ref.
  const isTaxonomyReady = useGlobalAssistantTaxonomyReady();

  const navigate = useNavigate();
  const { createPageDraft } = usePageManager();
  const { loggedInUser } = useGetLoggedInUser();
  const userTabs = useMemo(() => loggedInUser.tabs ?? [], [loggedInUser.tabs]);
  // Same source the chart menu gates its Download items on. Resolves async, so
  // it is `null` until the logged-in user lands — the core's `buildTurnContext`
  // only sends it when definitively `true` (see there).
  const { isTrialUser } = useUserTrialDetails<boolean>();
  // The mounted bridge's page filters, keyed by path. Absent when no bridge is
  // mounted (or its taxonomy has not settled) — the turn then simply carries no
  // page scope, exactly as before this field existed.
  const currentPageFiltersByPath = useGlobalAiCommandStore(
    (state) => state.currentPageFiltersByPath
  );
  // Mirrors `FilterProvider`'s gate exactly — an unresolved flag reads `false`,
  // but a bootstrap-cached `true` can land a render BEFORE `flagsReady`, and the
  // filter map only grows FilterId.ACTIVITY once BOTH are true. Claiming the
  // capability in that window would have the backend extract activity filters
  // the page cannot hold; omitting it there only costs activities on a turn
  // fired inside the hydration window. This is the single flag read for the
  // assistant — the turn-step context and the route-filter prefetch both take
  // their value from here (see the core's `buildTurnContext` /
  // `resolveRouteFiltersAndEmitApply`); which half applies is decided per
  // target entity in the core, since Company pages still gate the filter on
  // their own flag.
  const isUnleashReady = useUnleashReady();
  const liveActivityFilterFlags = useActivityFilterFlags();
  const activityFilterFlags = useMemo<ActivityFilterFlags>(
    () =>
      isUnleashReady
        ? liveActivityFilterFlags
        : { companyPages: false, otherPages: false },
    [isUnleashReady, liveActivityFilterFlags]
  );
  const allFlags = useUnleashFlags();
  const { config: reportBuilderConfig } = useReportBuilderConfig();
  const toast = useToast();

  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);

  // Per-MOUNT engine core instance (locked design): pending continuations and
  // the instance-owned slots die with the engine, exactly as the
  // component-local refs they replace did. Deps are refreshed in the render
  // body below via `engine.updateDeps` (H1 — never an effect: a click landing
  // between render and effects must still see fresh closures).
  const [engine] = useState(
    () =>
      new AssistantEngine({
        turnAbortRef,
        streamAbortRef,
        customDashAssistContextRef,
        conversationTrackingIdRef,
      })
  );

  // Pending unsaved-changes prompt: `true` while the in-app dialog is open.
  // In-app (never `window.confirm`) because the browser can suppress repeated
  // native dialogs — a suppressed confirm returns `false` instantly, silently
  // cancelling the redirect with no feedback (the invisible "clicked the build
  // link and nothing happened"). The RESOLVER lives on the engine
  // (`leaveConfirmSlot`), not in this state, so `dispose()` can settle a
  // dialog left open at unmount as "stay"; this state only drives the render.
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const confirmLeaveViaDialog = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      // A second guard while one dialog is open settles the FIRST as "stay"
      // (it can no longer be answered) and takes over the dialog.
      engine.leaveConfirmSlot.current?.resolve(false);
      engine.leaveConfirmSlot.current = { resolve };
      setIsLeaveConfirmOpen(true);
    });
  const settleLeaveConfirm = (leave: boolean) => {
    engine.leaveConfirmSlot.current?.resolve(leave);
    engine.leaveConfirmSlot.current = null;
    setIsLeaveConfirmOpen(false);
  };

  // NOTE: this component holds NO refs of its own. Async callbacks that
  // resolve after their creating render (e.g. the seeded-build landing
  // narration, up to 30s after the build click) read the transcript LIVE off
  // its zustand store, the pathname off `getCurrentRoute()`, the generation
  // status off the engine's `getGenerationStatus` dep, the taxonomy resolver
  // off the per-render `taxonomyByEntity` dep and `startAssistantTurn` off the
  // per-mount-stable engine instance — no mirror ref left anywhere.

  // The engine only mounts while an assistant surface is active (drawer open,
  // assistant homepage, or an existing conversation), so warm both the
  // backend and the headless digest path's data caches on mount — the
  // drawer-open gate the drawer used maps to "engine mounted" now.
  //
  // Deferred to browser idle: `warmHeadlessDigestPath` mounts a throwaway
  // synthetic chart to pre-pay the selection-list/taxonomy load (~17 queries),
  // and firing it during render made that whole stack compete with the page's
  // own first paint — worst on the assistant homepage, where the engine mounts
  // immediately. Idle runs it once the page has settled, so the first-question
  // latency win stays without blocking initial load. Both warm-ups are
  // idempotent (the `hasRequested` guard and `warmUpFired`), so a re-render
  // rescheduling the callback can't double-fire.
  useEffect(() => {
    const runWarmUps = () => {
      requestGlobalAssistantWarmUpOnce({
        isDrawerOpen: true,
        hasRequested: hasRequestedWarmUp,
        markRequested: markWarmUpRequested,
        onWarmUpError: (error) => {
          console.debug('Global assistant warm-up failed', error);
        },
        onWarmUpResult: (response) => {
          console.debug('Global assistant warm-up response', response);
        },
        requestWarmUp: postGlobalAssistantWarmUp,
      });
      warmHeadlessDigestPath();
    };

    // Safari gained requestIdleCallback in 16.4; setTimeout is the fallback.
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(runWarmUps, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(runWarmUps, 1500);
    return () => window.clearTimeout(handle);
  }, [hasRequestedWarmUp, markWarmUpRequested]);

  // --- Command-channel drain shells --------------------------------------
  // The drain logic and the seq watermarks live on the engine core; these two
  // effects keep only the REACT SCHEDULING, which is load-bearing (H4). They
  // must stay effects rather than store subscriptions: a subscription fires at
  // PUBLISH time, and both drains assume post-COMMIT values (the adjustment
  // drain reads the live pathname and the turn record, and its hold-until-
  // turn-end depends on re-running when `isTurnRunning` flips).

  // Failed optimistic filter applies (TTL expiry / bridge error) surfaced in
  // chat — re-runs whenever the channel publishes.
  const filterApplyFailureChannel = useGlobalAiCommandStore(
    (state) => state.channels.filterApplyFailure
  );
  // Subscribed, not read at fire time: the drain's watermark is keyed by the
  // conversation (see `filterApplyFailureWatermark` in the core), and the
  // re-seed must land AT the id change — an effect that only woke on a publish
  // would first notice a new conversation as that conversation's FIRST failure
  // arrived, and re-seed past it.
  const conversationId = useGlobalAiConversationStateStore((s) => s.trackingId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the channel and the conversation id are the triggers; the drain reads the channel live off the store
  useEffect(() => {
    engine.consumeFilterApplyFailures(conversationId);
  }, [engine, filterApplyFailureChannel, conversationId]);

  // Filter cap corrections, HELD until the turn that made the claim ends —
  // hence `isTurnRunning` as a re-run trigger (the engine re-checks it and
  // leaves the watermark unadvanced while a turn runs).
  //
  // ONE TRIGGER WAS DROPPED, deliberately: this effect used to also list
  // `location.pathname`, so a navigation re-ran it and could release a held
  // correction on arrival at a new page. The drain now reads the route live
  // via `getCurrentRoute()`, so a re-run on navigation buys nothing — and the
  // correction it would have released is one whose page the user has just
  // LEFT, which the drain's own path check discards anyway. Strictly fewer,
  // never-useful firings; the H4 invariant above (commit-time, not
  // publish-time) is untouched.
  const filterApplyAdjustmentChannel = useGlobalAiCommandStore(
    (state) => state.channels.filterApplyAdjustment
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: both the channel and the turn-running flip are publish/commit triggers; the drain reads them live off the stores
  useEffect(() => {
    engine.consumeFilterApplyAdjustments();
  }, [engine, filterApplyAdjustmentChannel, isTurnRunning]);

  useGlobalAiTurnContinuation({
    isTurnRunning,
    // The instance is per-mount stable, so this bound method is always the
    // freshest closure — no ref indirection needed for the ≤30s-later fire.
    startAssistantTurn: engine.startAssistantTurn,
  });

  // --- Tool-loop runner wiring + engine-core dep refresh -----------------
  // The runner drives a turn through the AI-assistant tool loop. Its dep set
  // lives on `AssistantEngine` (assistant-engine-core.ts) so the loop's client
  // tools (navigate / apply_filters / update_current_page / create_custom_page
  // / offer_report-as-chip) run through the exact same command-store + page
  // machinery the legacy pipeline used — but unit-testable without a React
  // renderer. The hook only STORES these per-mount-stable bound methods (it
  // calls none at render time), so it can run before the engine's deps are
  // installed and hand back `runTurn` for the single `updateDeps` below.
  const { runTurn } = useGlobalAiTurnRunner({
    onToolOutcome: engine.onToolOutcome,
    resolveAndEmitRouteFilters: engine.resolveAndEmitRouteFilters,
    emitResolvedRouteFilters: engine.emitResolvedRouteFilters,
    navigateToOptionId: engine.navigateToOptionId,
    emitUpdateCurrentPage: engine.emitUpdateCurrentPage,
    emitCreateCurrentPage: engine.emitCreateCurrentPage,
    headlessBuildTaxonomy: engine.headlessBuildTaxonomy,
    offerReport: engine.offerReportFromLoop,
    currentPageLabel: engine.currentPageLabel,
    inferEntityTypeForPath: engine.inferEntityTypeForPath,
    buildTurnContext: engine.buildTurnContext,
  });

  // H1: this dep refresh happens IN THE RENDER BODY (never an effect), so a
  // click landing between render and effects still sees fresh closures.
  engine.updateDeps({
    getCurrentRoute,
    taxonomyByEntity,
    // The transcript's source of truth is its own zustand store (the context
    // just subscribes), so the core reads it live — no mirror ref.
    getTranscript: () =>
      useGlobalAiConversationStateStore.getState().transcript,
    appendTranscript,
    clearTranscript,
    updateReasoningTrace,
    updateAssistantMessageText,
    setGenerationStatus,
    // Closure over THIS render's status value — the TTL status-clear timers
    // read it long after the click, and every render installs a newer getter.
    getGenerationStatus: () => generationStatus,
    isTaxonomyReady,
    lastUserPrompt,
    setLastUserPrompt,
    conversationSummary,
    sessionEntityScope,
    userTabs,
    reportBuilderConfig,
    allFlags,
    activityFilterFlags,
    isTrialUser,
    currentPageFiltersByPath,
    toast,
    navigate,
    createPageDraft,
    confirmLeave: confirmLeaveViaDialog,
    runTurn,
  });

  // Publish the engine. The INSTANCE itself is the `GlobalAiEngineApi` the
  // skins consume: per-mount stable (so store subscribers never churn on an
  // engine re-render) and always the freshest closures (its deps were just
  // refreshed above), which is what retired the per-render delegate object
  // this replaced.
  useEffect(() => {
    // Re-arm the engine-abort controller before anything can publish a turn
    // (H2): a remount that reuses this instance — StrictMode's simulated
    // remount, which RTL enables — otherwise leaves the cleanup's abort in
    // place and every later gated turn skips `runTurn` silently. `onMount`
    // also clears the disposed flag, so mount → dispose → mount composes.
    engine.onMount();
    useGlobalAiEngineStore.setState({ api: engine });
    // Teardown nulls the store's `api` field again (the skins' `?.` guard and
    // persistence's "engine mounted" signal both depend on that) and resets
    // the turn record — see `AssistantEngine.dispose`.
    return () => engine.dispose();
    // `engine` is per-mount stable (useState, never re-set), so this dep
    // keeps the effect mount/unmount-only exactly as the old `[]` did.
  }, [engine]);

  return (
    <UnsavedChangesDialog
      isOpen={isLeaveConfirmOpen}
      onStay={() => settleLeaveConfirm(false)}
      onLeave={() => settleLeaveConfirm(true)}
    />
  );
}
